(() => {
  const CLEANUP_KEY = "__valyrxVisualV3Cleanup";
  const scriptUrl = document.currentScript?.src || window.location.href;
  const fontStylesheetUrl = new URL("valyrx-v3-fonts.css", scriptUrl).href;
  let fontStylesheetPromise;

  const ensureFonts = () => {
    const existing = document.querySelector("link[data-valyrx-v3-fonts]");
    if (existing?.dataset.loaded === "true") return Promise.resolve(existing);
    if (fontStylesheetPromise) return fontStylesheetPromise;

    const link = existing || document.createElement("link");
    link.rel = "stylesheet";
    link.href = fontStylesheetUrl;
    link.dataset.valyrxV3Fonts = "true";
    link.fetchPriority = "low";

    fontStylesheetPromise = new Promise((resolve) => {
      link.addEventListener("load", () => {
        link.dataset.loaded = "true";
        resolve(link);
      }, { once: true });
      link.addEventListener("error", () => resolve(link), { once: true });
    });

    if (!existing) document.head.append(link);
    return fontStylesheetPromise;
  };

  const getScroller = () => {
    const pageWrapper = document.querySelector(".page-wrapper");
    if (!pageWrapper) return window;

    const styles = window.getComputedStyle(pageWrapper);
    const canScroll = /(auto|scroll)/.test(styles.overflowY)
      && pageWrapper.scrollHeight > pageWrapper.clientHeight + 1;

    return canScroll ? pageWrapper : window;
  };

  const padIndex = (value) => String(value).padStart(2, "0");

  const initRail = (rail) => {
    const track = rail.querySelector("[data-valyrx-rail-track]");
    const cards = [...track?.querySelectorAll("[data-valyrx-card]") || []];
    const previous = rail.querySelector("[data-valyrx-rail-prev]");
    const next = rail.querySelector("[data-valyrx-rail-next]");
    const current = rail.querySelector("[data-valyrx-rail-current]");
    const progress = rail.querySelector("[data-valyrx-rail-progress]");

    if (!track || cards.length === 0) return () => {};

    const controller = new AbortController();
    const { signal } = controller;
    let frame = 0;

    const cardStep = () => {
      const cardRect = cards[0].getBoundingClientRect();
      const styles = window.getComputedStyle(track);
      return cardRect.width + (Number.parseFloat(styles.columnGap || styles.gap) || 0);
    };

    const update = () => {
      frame = 0;
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      const ratio = maxScroll > 0 ? Math.min(1, Math.max(0, track.scrollLeft / maxScroll)) : 1;
      const visibleIndex = Math.min(cards.length - 1, Math.max(0, Math.round(track.scrollLeft / Math.max(1, cardStep()))));

      if (current) current.textContent = padIndex(visibleIndex + 1);
      if (progress) progress.style.transform = `scaleX(${ratio})`;
      if (previous) previous.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= maxScroll - 2;
    };

    const queueUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    const move = (direction) => {
      track.scrollBy({ left: cardStep() * direction, behavior: "smooth" });
    };

    previous?.addEventListener("click", () => move(-1), { signal });
    next?.addEventListener("click", () => move(1), { signal });
    track.addEventListener("scroll", queueUpdate, { passive: true, signal });
    track.addEventListener("keydown", (event) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      move(event.key === "ArrowRight" ? 1 : -1);
    }, { signal });
    window.addEventListener("resize", queueUpdate, { passive: true, signal });

    update();
    return () => {
      controller.abort();
      if (frame) window.cancelAnimationFrame(frame);
    };
  };

  const initFontLoading = () => {
    const mobile = window.matchMedia("(max-width: 620px)").matches;
    const target = mobile
      ? document.querySelector("[data-valyrx-world-index] .valyrx-world-index__worlds, .valyrx-world-index--page .valyrx-world-index__worlds")
      : document.querySelector("[data-valyrx-world-index], .valyrx-world-index--page");
    if (!target || !("IntersectionObserver" in window)) {
      ensureFonts();
      return () => {};
    }

    const scroller = getScroller();
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      ensureFonts();
      observer.disconnect();
    }, {
      root: scroller === window ? null : scroller,
      rootMargin: mobile ? "0px" : "800px 0px",
    });
    observer.observe(target);
    return () => observer.disconnect();
  };

  const initMotion = () => {
    if (!window.gsap || !window.ScrollTrigger) return () => {};

    window.gsap.registerPlugin(window.ScrollTrigger);
    const scroller = getScroller();
    const scrollTriggerScroller = scroller === window ? undefined : scroller;
    const media = window.gsap.matchMedia();

    media.add({
      desktop: "(min-width: 990px)",
      tablet: "(min-width: 621px) and (max-width: 989px)",
      mobile: "(max-width: 620px)",
      reduce: "(prefers-reduced-motion: reduce)",
    }, (mediaContext) => {
      if (mediaContext.conditions.reduce) return undefined;
      const revealDistance = mediaContext.conditions.desktop ? 34 : mediaContext.conditions.tablet ? 24 : 18;

      const context = window.gsap.context(() => {
        document.querySelectorAll("[data-valyrx-world-index]").forEach((worldIndex) => {
          const planes = worldIndex.querySelectorAll(".valyrx-world-index__media");
          const labels = worldIndex.querySelectorAll(".valyrx-world-index__meta");

          window.gsap.fromTo(planes,
            { clipPath: "inset(0 0 100% 0)" },
            {
              clipPath: "inset(0 0 0% 0)",
              duration: 0.92,
              stagger: 0.08,
              ease: "power3.out",
              scrollTrigger: {
                trigger: worldIndex,
                scroller: scrollTriggerScroller,
                start: "top 82%",
                once: true,
              },
            });

          window.gsap.from(labels, {
            y: revealDistance,
            autoAlpha: 0,
            duration: 0.72,
            stagger: 0.07,
            ease: "power3.out",
            scrollTrigger: {
              trigger: worldIndex,
              scroller: scrollTriggerScroller,
              start: "top 76%",
              once: true,
            },
          });
        });

        document.querySelectorAll("[data-valyrx-world-transition]").forEach((transition) => {
          const mode = transition.dataset.valyrxWorldTransition;
          const signal = transition.querySelector(".valyrx-world-transition__signal");
          const planes = transition.querySelectorAll(".valyrx-world-transition__plane");
          const marker = transition.querySelector(".valyrx-world-transition__marker");
          const artwork = transition.querySelector(".valyrx-world-transition__artwork");
          const scan = transition.querySelector(".valyrx-world-transition__scan");
          if (!signal || !marker || !artwork) return;

          const timeline = window.gsap.timeline({
            scrollTrigger: {
              trigger: transition,
              scroller: scrollTriggerScroller,
              start: "top bottom",
              end: "bottom top",
              scrub: 0.5,
              invalidateOnRefresh: true,
            },
          });

          timeline
            .fromTo(signal,
              { scaleX: 0.018 },
              { scaleX: 1, duration: 0.24, ease: "power2.inOut" }, 0.08)
            .to(planes, {
              clipPath: "inset(0 0 0 96%)",
              duration: 0.34,
              stagger: 0.04,
              ease: "power2.inOut",
            }, 0.2)
            .fromTo(marker,
              { autoAlpha: 0, x: -14 },
              { autoAlpha: 1, x: 0, duration: 0.16, ease: "power3.out" }, 0.27)
            .to(marker, { autoAlpha: 0, duration: 0.12 }, 0.52)
            .fromTo(artwork,
              { autoAlpha: 0, scale: 0.94 },
              { autoAlpha: mode === "collapse" ? 0.72 : 0.82, scale: 1, duration: 0.3, ease: "power3.out" }, 0.44)
            .to(signal, {
              scaleX: mode === "collapse" ? 0.008 : 0.025,
              boxShadow: mode === "strip"
                ? "-7px 0 0 rgba(0, 232, 239, 0.78), 7px 0 0 rgba(232, 24, 136, 0.72)"
                : "0 0 20px rgba(165, 0, 0, 0.58)",
              duration: 0.22,
              ease: "power2.inOut",
            }, 0.62);

          if (scan) {
            timeline.fromTo(scan,
              { autoAlpha: 0, xPercent: 0 },
              { autoAlpha: 0.72, xPercent: 900, duration: 0.24, ease: "power2.inOut" }, 0.38)
              .to(scan, { autoAlpha: 0, duration: 0.08 }, 0.62);
          }
        });

        document.querySelectorAll(".valyrx-rail-heading").forEach((heading) => {
          window.gsap.from(heading.children, {
            y: revealDistance,
            autoAlpha: 0,
            duration: 0.72,
            stagger: 0.08,
            ease: "power3.out",
            scrollTrigger: {
              trigger: heading,
              scroller: scrollTriggerScroller,
              start: "top 88%",
              once: true,
            },
          });
        });
      });

      return () => context.revert();
    });

    window.requestAnimationFrame(() => window.ScrollTrigger.refresh());
    return () => media.revert();
  };

  const init = () => {
    window[CLEANUP_KEY]?.();

    const cleanups = [
      ...[...document.querySelectorAll("[data-valyrx-rail]")].map(initRail),
      initFontLoading(),
      initMotion(),
    ];

    document.documentElement.dataset.valyrxV3Ready = "true";
    window[CLEANUP_KEY] = () => {
      cleanups.reverse().forEach((cleanup) => cleanup?.());
      delete document.documentElement.dataset.valyrxV3Ready;
    };
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", init);
  document.addEventListener("shopify:section:unload", () => window[CLEANUP_KEY]?.());
  document.addEventListener("shopify:section:reorder", init);
})();
